import { type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import CheckShield from 'dibs-icons/exports/CheckShield.ts';

import { useBuyerIncentivesMessages } from './hooks/useBuyerIncentivesMessages.tsx';

export const Incentives: FC = () => {
    const incentives = useBuyerIncentivesMessages();

    return (
        <>
            {incentives.map((incentive, index) => (
                <div key={index} className={classnames(dibsCss.inline, dibsCss.whitespacePreWrap)}>
                    <FormattedMessage
                        id="PdpSharedSidebarBuyerIncentives.incentive"
                        defaultMessage="{isLast, select, true {{incentive}} other {{incentive}, }}"
                        values={{ incentive, isLast: index === incentives.length - 1 }}
                    />
                </div>
            ))}
        </>
    );
};

const getDetailsLink = (getLink: (chunk: ReactNode) => ReactNode): ReactNode =>
    getLink(<FormattedMessage id="BuyerIncentivesBanner.detailsLink" defaultMessage="Details" />);

type Props = {
    getLink?: (chunk: ReactNode) => ReactNode;
    linkPosition?: 'top' | 'bottom';
    isMobile?: boolean;
    isTrustMessagingTest2Variant?: boolean;
    variant?: 'default' | 'purchaseProtection';
};

export const BuyerIncentivesBanner: FC<Props> = ({
    getLink,
    linkPosition = 'bottom',
    isMobile = false,
    isTrustMessagingTest2Variant,
    variant = 'default',
}) => {
    if (variant === 'purchaseProtection') {
        return (
            <div
                className={classnames(
                    dibsCss.pSmaller,
                    dibsCss.bgShagreen50,
                    dibsCss.roundedXxsmall,
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.itemsStart
                )}
            >
                <span className={dibsCss.shrink0}>
                    <CheckShield variation="24" />
                </span>
                <span className={dibsCss.sassyFontBodySmall}>
                    <FormattedMessage
                        id="BuyerIncentivesBanner.purchaseProtection.message"
                        defaultMessage="Only orders placed on 1stDibs are protected if an item is damaged, isn't authentic, or isn't as described."
                    />
                </span>
            </div>
        );
    }

    const detailsLink = getLink ? getDetailsLink(getLink) : null;

    if (isTrustMessagingTest2Variant) {
        return (
            <div
                className={classnames(
                    isMobile ? dibsCss.pXsmall : dibsCss.pSmaller,
                    dibsCss.roundedXxsmall,
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.itemsStart,
                    dibsCss.bgCyan100
                )}
            >
                <span className={classnames(dibsCss.flex, dibsCss.fillBlue600)}>
                    <CheckShield variation={isMobile ? '24' : '40'} />
                </span>
                <div>
                    <div
                        className={classnames(dibsCss.sassyFontBodyMediumHeavy, {
                            [dibsCss.mbXxsmall]: !isMobile,
                        })}
                    >
                        <FormattedMessage
                            id="BuyerIncentivesBanner.incentives.protectionTitle"
                            defaultMessage="1stDibs Purchase Protection"
                        />
                    </div>
                    <span className={dibsCss.sassyFontBodySmall}>
                        <FormattedMessage
                            id="BuyerIncentivesBanner.incentives.descriptionWithLink"
                            defaultMessage="If your item is inauthentic, not as described, or damaged, you get your money back. {detailsLink}"
                            values={{
                                detailsLink,
                            }}
                        />
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={classnames(dibsCss.pSmaller, dibsCss.bgShagreen50, dibsCss.roundedXxsmall)}>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.sassyFontBodySmallHeavy,
                    dibsCss.mbXxsmall
                )}
            >
                <CheckShield variation="20" />
                <FormattedMessage
                    id="BuyerIncentivesBanner.incentives.title"
                    defaultMessage="Shop With Confidence {showLink, select, true {{detailsLink}} other {}}"
                    values={{
                        showLink: linkPosition === 'top',
                        detailsLink,
                    }}
                />
            </div>
            <span className={dibsCss.sassyFontBodySmall}>
                <FormattedMessage
                    id="BuyerIncentivesBanner.incentives.message"
                    defaultMessage="{incentives} {showLink, select, true {{detailsLink}} other {}}"
                    values={{
                        incentives: <Incentives />,
                        showLink: linkPosition === 'bottom',
                        detailsLink,
                    }}
                />
            </span>
        </div>
    );
};
